import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * DESIGN.md › list-row: a white settings
 * row — leading icon, label, an optional value on the right, then the trailing control the
 * app supplies (chevron, switch, sign-out icon). Text arrives translated. Without `onClick`
 * the row is static, so a switch inside it stays the only control.
 */
export function ListRow({
  icon,
  label,
  value,
  accent = false,
  destructive = false,
  trailing,
  onClick,
  disabled = false,
  accessibilityLabel,
  className,
}: {
  icon?: ReactNode;
  label: string;
  value?: string;
  /** The value in the action colour instead of grey — the time on the daily-reminder card. */
  accent?: boolean;
  /** The label in the destructive red — "Delete group". */
  destructive?: boolean;
  trailing?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  className?: string;
}) {
  const pressable = onClick !== undefined && !disabled;
  const body = (
    <>
      {icon}
      <span
        className={cn(
          'flex-1 truncate text-left text-label-md',
          destructive ? 'text-action-destructive' : 'text-on-surface',
        )}
      >
        {label}
      </span>
      {/* Figma row/trailingDisplay: the value in the weak grey, so the label stays the loud part. */}
      {value ? (
        <span
          className={cn(
            'truncate text-body-sm font-bold',
            accent ? 'text-on-action-secondary' : 'text-on-surface-weak',
          )}
        >
          {value}
        </span>
      ) : null}
      {trailing}
    </>
  );
  const classes = cn(
    'flex min-h-tap-target w-full items-center gap-internal-lg border-b border-border-weak bg-surface py-internal-lg pr-internal-md pl-internal-lg font-brand',
    pressable &&
      'cursor-pointer hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-border-highlight',
    className,
  );
  if (pressable) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={accessibilityLabel ?? (value ? `${label}, ${value}` : label)}
        className={classes}
      >
        {body}
      </button>
    );
  }
  return <div className={classes}>{body}</div>;
}
